import * as Yup from 'yup';
import { AWS_REGIONS } from './constants';
import {
  CIDREditMode,
  EditVPCConnectionPayload,
  VPCPeeringConnection,
  VPCPeeringEditableField,
  VPCPeeringFormMode
} from '../../interface';

// Single CIDR block: octets 0-255 and a /0-32 prefix, e.g. 172.31.0.0/16
const OCTET = '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';
const CIDR = `${OCTET}(?:\\.${OCTET}){3}/(?:3[0-2]|[12]?\\d)`;
// One or more comma-separated CIDR blocks (optional spaces around commas)
const CIDR_LIST_REGEX = new RegExp(`^${CIDR}(?:\\s*,\\s*${CIDR})*$`);

export const initialValues = {
  name: '',
  vpc_id: '',
  cidr_range: '',
  aws_account_id: '',
  aws_region: AWS_REGIONS[0].value
};

export type VPCPeeringFormValues = typeof initialValues;

/** Form field names, which differ from the API's `editable_fields` wire names. */
export enum VPCPeeringFormField {
  NAME = 'name',
  VPC_ID = 'vpc_id',
  CIDR_RANGE = 'cidr_range',
  AWS_ACCOUNT_ID = 'aws_account_id',
  AWS_REGION = 'aws_region'
}

// The form binds a single comma-separated text field; the API takes a list.
const FORM_FIELD_TO_API_FIELD: Partial<Record<VPCPeeringFormField, VPCPeeringEditableField>> = {
  [VPCPeeringFormField.NAME]: VPCPeeringEditableField.NAME,
  [VPCPeeringFormField.CIDR_RANGE]: VPCPeeringEditableField.CIDR,
  [VPCPeeringFormField.AWS_REGION]: VPCPeeringEditableField.AWS_REGION
};

export const parseCIDRList = (value: string): string[] =>
  value
    .split(',')
    .map(cidr => cidr.trim())
    .filter(Boolean);

// Explicit comparator: the default sort coerces to string and compares UTF-16
// code units, which is only incidentally right here. Order just has to be
// stable and identical on both sides of the comparison.
const normaliseCIDRList = (cidrs: string[]): string[] =>
  [...new Set(cidrs)].sort((a, b) => a.localeCompare(b));

export const getInitialValues = (
  connection?: VPCPeeringConnection | null
): VPCPeeringFormValues => {
  if (!connection) {
    return initialValues;
  }

  return {
    name: connection.name ?? '',
    vpc_id: connection.vpc_id,
    cidr_range: (connection.cidr ?? []).join(', '),
    aws_account_id: connection.aws_account_id,
    aws_region: connection.aws_region
  };
};

/**
 * Editability comes from the server on every record — never from the status.
 * `vpc_id` and `aws_account_id` have no API counterpart and so are only ever
 * editable while creating.
 */
export const isFieldEditable = (
  field: VPCPeeringFormField,
  mode: VPCPeeringFormMode,
  connection?: VPCPeeringConnection | null
): boolean => {
  if (mode === VPCPeeringFormMode.CREATE) {
    return true;
  }

  const apiField = FORM_FIELD_TO_API_FIELD[field];
  if (!apiField) {
    return false;
  }

  return (connection?.editable_fields ?? []).includes(apiField);
};

export const getValidationSchema = (
  mode: VPCPeeringFormMode,
  connection?: VPCPeeringConnection | null
) => {
  // `disabled` is a DOM attribute, so Formik still validates a locked field.
  // A record carrying `name: null` or a malformed stored CIDR would fail
  // validation the user cannot see or fix, and Formik resolves submitForm()
  // without throwing — a dead CTA. Only validate what can be submitted.
  const ruleFor = <T extends Yup.Schema<unknown>>(field: VPCPeeringFormField, rule: T) =>
    isFieldEditable(field, mode, connection) ? rule : Yup.string().notRequired();

  const cidrRule = Yup.string()
    .trim()
    .matches(
      CIDR_LIST_REGEX,
      'Enter valid CIDR(s), comma-separated. e.g. 172.31.0.0/16, 10.0.0.0/24'
    )
    .required();

  const isAdditiveOnly =
    mode !== VPCPeeringFormMode.CREATE &&
    connection?.cidr_edit_mode === CIDREditMode.ADDITIVE_ONLY;
  const currentCIDRs = connection?.cidr ?? [];

  return Yup.object({
    name: ruleFor(VPCPeeringFormField.NAME, Yup.string().trim().required()),
    vpc_id: ruleFor(VPCPeeringFormField.VPC_ID, Yup.string().trim().required()),
    // On a live peering an omitted CIDR is a removal, which deletes a route the
    // connection is currently serving. Catch it here rather than on the 400.
    cidr_range: ruleFor(
      VPCPeeringFormField.CIDR_RANGE,
      isAdditiveOnly
      ? cidrRule.test(
          'no-cidr-removals',
          () =>
            `Existing CIDRs cannot be removed from a live peering — keep ${currentCIDRs.join(
              ', '
            )} and add to the list. Removing one would delete a live route.`,
          value => {
            const submitted = parseCIDRList(value ?? '');
            return currentCIDRs.every(existing => submitted.includes(existing));
          }
        )
        : cidrRule
    ),
    aws_account_id: ruleFor(
      VPCPeeringFormField.AWS_ACCOUNT_ID,
      Yup.string()
        .trim()
        .matches(/^\d{12}$/, 'AWS Account ID must be exactly 12 digits')
        .required()
    ),
    aws_region: ruleFor(VPCPeeringFormField.AWS_REGION, Yup.string().required())
  });
};

/**
 * Builds the PATCH/resubmit body from what actually changed, filtered to what
 * the server says is editable. Sending a field outside `editable_fields` is a
 * 400, so an unchanged field is simply omitted — absent means unchanged.
 */
export const buildEditPayload = (
  values: VPCPeeringFormValues,
  mode: VPCPeeringFormMode,
  connection: VPCPeeringConnection
): EditVPCConnectionPayload => {
  const payload: EditVPCConnectionPayload = {};

  if (
    isFieldEditable(VPCPeeringFormField.NAME, mode, connection) &&
    values.name.trim() !== (connection.name ?? '').trim()
  ) {
    payload.name = values.name.trim();
  }

  if (isFieldEditable(VPCPeeringFormField.CIDR_RANGE, mode, connection)) {
    // A reorder is not a change, and a duplicated paste is a 400 on the route
    // request rather than merely redundant — normalise before comparing.
    const cidr = normaliseCIDRList(parseCIDRList(values.cidr_range));
    if (cidr.join(',') !== normaliseCIDRList(connection.cidr ?? []).join(',')) {
      payload.cidr = cidr;
    }
  }

  if (
    isFieldEditable(VPCPeeringFormField.AWS_REGION, mode, connection) &&
    values.aws_region !== connection.aws_region
  ) {
    payload.aws_region = values.aws_region;
  }

  return payload;
};
